import { storeUtils } from '../../data/storeData';

/**
 * 口座振替情報表示コンポーネント（ステップ形式版）
 * 銀行振込情報と口座振替手続きの案内をステップ形式で表示
 * 再利用可能なコンポーネントとして設計
 */
const BankTransferInfo = ({ 
  showTitle = true,
  storeName = null,
  showAmount = true,
  containerStyle = {},
  className = ''
}) => {
  // 店舗情報から金額情報を取得
  const costInfo = storeName ? storeUtils.getStoreCostInfo(storeName) : null;
  // スタイル定義
  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e0e0e0',
      ...containerStyle
    },
    title: {
      fontSize: '20px',
      fontWeight: 'bold',
      marginBottom: '20px',
      color: '#333',
      textAlign: 'center',
      borderBottom: '2px solid #007bff',
      paddingBottom: '10px'
    },
    stepContainer: {
      marginBottom: '30px',
      border: '2px solid #007bff',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    stepHeader: {
      backgroundColor: '#007bff',
      color: 'white',
      padding: '15px 20px',
      fontSize: '16px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    stepNumber: {
      backgroundColor: 'white',
      color: '#007bff',
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: '14px'
    },
    stepContent: {
      padding: '20px',
      backgroundColor: '#f8f9fa'
    },
    // 金額情報表示
    amountInfoContainer: {
      backgroundColor: '#fff3cd',
      border: '2px solid #ffc107',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '20px',
      textAlign: 'center'
    },
    amountTitle: {
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#856404',
      marginBottom: '10px'
    },
    amountValue: {
      fontSize: '25px',
      fontWeight: 'bold',
      color: '#856404',
      marginBottom: '8px'
    },
    amountBreakdown: {
      fontSize: '14px',
      color: '#856404',
      fontWeight: '500'
    },
    bankInfoContainer: {
      backgroundColor: 'white',
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      padding: '20px',
      marginBottom: '15px'
    },
    bankInfoRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '12px',
      fontSize: '14px'
    },
    bankInfoLabel: {
      fontWeight: 'bold',
      width: '100px',
      color: '#495057',
      flexShrink: 0
    },
    bankInfoValue: {
      color: '#212529',
      fontWeight: '600',
      fontSize: '14px'
    },
    noteContainer: {
      backgroundColor: '#fff3cd',
      border: '2px solid #ffc107',
      borderRadius: '6px',
      padding: '15px',
      marginBottom: '15px'
    },
    noteText: {
      color: '#856404',
      fontSize: '14px',
      fontWeight: 'bold',
      lineHeight: '1.6',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      textAlign: 'center'
    },
    noteIcon: {
      fontSize: '18px'
    },
    procedureContainer: {
      backgroundColor: 'white',
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      padding: '20px',
      marginBottom: '15px'
    },
    procedureText: {
      marginBottom: '15px',
      fontSize: '14px',
      lineHeight: '1.6',
      color: '#495057'
    },
    linkContainer: {
      backgroundColor: '#e7f3ff',
      border: '2px solid #007bff',
      borderRadius: '6px',
      padding: '15px',
      textAlign: 'center',
      marginBottom: '15px'
    },
    linkLabel: {
      fontSize: '13px',
      fontWeight: 'bold',
      color: '#007bff',
      marginBottom: '8px',
      display: 'block'
    },
    link: {
      color: '#007bff',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: 'bold',
      wordBreak: 'break-all'
    },
    warningContainer: {
      backgroundColor: '#f8d7da',
      border: '1px solid #f5c6cb',
      borderRadius: '6px',
      padding: '15px'
    },
    warningText: {
      color: '#721c24',
      fontSize: '13px',
      lineHeight: '1.5',
      margin: 0
    }
  };

  return (
    <div style={styles.container} className={className}>
      {showTitle && (
        <h2 style={styles.title}>お支払い情報</h2>
      )}
      
      {/* ステップ1: 初期費用のお振込み先 */}
      <div style={styles.stepContainer}>
        <div style={styles.stepHeader}>
          <div style={styles.stepNumber}>1</div>
          <span>初期費用のお振込み先</span>
        </div>
        <div style={styles.stepContent}>

          {/* 振込金額表示 */}
          {showAmount && costInfo && (
            <div style={styles.amountInfoContainer}>
              <div style={styles.amountTitle}>お振込金額</div>
              <div style={styles.amountValue}>
                {`${costInfo.cost.toLocaleString()}円（税込）`}
              </div>
              <div style={styles.amountBreakdown}>
                {`内訳：${costInfo.description ?? ''}`}
              </div>
            </div>
          )}

          <div style={styles.bankInfoContainer}>
            <div style={styles.bankInfoRow}>
              <span style={styles.bankInfoLabel}>銀行名</span>
              <span style={styles.bankInfoValue}>りそな銀行</span>
            </div>
            <div style={styles.bankInfoRow}>
              <span style={styles.bankInfoLabel}>支店名</span>
              <span style={styles.bankInfoValue}>上野支店</span>
            </div>
            <div style={styles.bankInfoRow}>
              <span style={styles.bankInfoLabel}>口座種別</span>
              <span style={styles.bankInfoValue}>普通預金</span>
            </div>
            <div style={styles.bankInfoRow}>
              <span style={styles.bankInfoLabel}>口座番号</span>
              <span style={styles.bankInfoValue}>1582467</span>
            </div>
            <div style={styles.bankInfoRow}>
              <span style={styles.bankInfoLabel}>口座名義</span>
              <span style={styles.bankInfoValue}>カ）イシハラフドウサン（株式会社石原不動産）</span>
            </div>
          </div>

          {/* 振込時の注意点 */}
          <div style={styles.noteContainer}>
            <p style={styles.noteText}>
              <span style={styles.noteIcon}>⚠️</span>
              <span>【重要】お振込名義は、必ず会員登録者ご本人様の名義でお手続きください。</span>
            </p>
          </div>
        </div>
      </div>

      {/* ステップ2: 口座振替お手続きの流れ（所要時間：約3分） */}
      <div style={styles.stepContainer}>
        <div style={styles.stepHeader}>
          <div style={styles.stepNumber}>2</div>
          <span>口座振替お手続きの流れ（所要時間：約3分）</span>
        </div>
        <div style={styles.stepContent}>
          <div style={styles.procedureContainer}>
            <div style={styles.procedureText}>
              下記の登録ページにアクセスし、画面の案内に沿って入力を進めてください。
            </div>
          </div>

          {/* 登録ページリンク */}
          <div style={styles.linkContainer}>
            <span style={styles.linkLabel}>口座振替登録ページ：</span>
            <a 
              href="https://taku-tore.com/dd-entry" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={styles.link}
            >
              https://taku-tore.com/dd-entry
            </a>
          </div>

          {/* 重要な注意点 */}
          <div style={styles.warningContainer}>
            <p style={styles.warningText}>
              <span>※登録には、ご本人確認のための情報入力が必要となります。</span><br />
              <span>ご利用の金融機関により手順が異なります。</span><br />
              <span>正常に登録が完了した場合、「正常に完了しました」の画面が表示されますので、必ずご確認ください。</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankTransferInfo;